import React, { ReactNode } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import { b } from 'utils/i18nUtils';
import { getUrlParam } from 'utils/urlUtils';

import { Icon, StatusCard } from 'components/ui';

enum OriginEnum {
  PXL_VISION = 'pxl-vision',
  PAYMENT_SUCCESS = 'payment-success',
  PAYMENT_FAILURE = 'payment-failure',
}

type StatusType = 'success' | 'failure';

type OriginConfig = {
  status: StatusType;
  icon: React.ReactNode;
  title: MessageDescriptor;
  description: MessageDescriptor & { values?: Record<string, (text: ReactNode) => ReactNode> };
};

const ORIGIN_CONFIG: Record<OriginEnum, OriginConfig> = {
  [OriginEnum.PXL_VISION]: {
    status: 'success',
    icon: <Icon className="fb-icon--check text-4xl text-white" />,
    title: { id: 'page.confirmation.pxl-vision.title', defaultMessage: 'Verification is complete.' },
    description: {
      id: 'page.confirmation.pxl-vision.description',
      defaultMessage: 'Please close this tab and continue on your computer.'
    },
  },
  [OriginEnum.PAYMENT_SUCCESS]: {
    status: 'success',
    icon: <Icon className="fb-icon--check text-4xl text-white" />,
    title: { id: 'page.confirmation.paymentSuccess.title', defaultMessage: 'Payment completed' },
    description: {
      id: 'page.confirmation.paymentSuccess.description',
      defaultMessage: 'Thank you for your payment! You can now <b>continue</b> your check-out <b>on the kiosk.</b>',
      values: { b: (text: ReactNode) => b(text, 'text-default text-lg') },
    },
  },
  [OriginEnum.PAYMENT_FAILURE]: {
    status: 'failure',
    icon: <Icon className="fb-icon--close text-4xl text-white" />,
    title: { id: 'page.confirmation.paymentFailure.title', defaultMessage: 'Payment failure' },
    description: {
      id: 'page.confirmation.paymentFailure.description',
      // eslint-disable-next-line max-len
      defaultMessage: 'Unfortunately we were unable to process the payment. Please <b>go back</b> to the <b>payment link</b> and try again.',
      values: { b: (text: ReactNode) => b(text, 'text-default text-lg') },
    },
  },
};

const DEFAULT_CONFIG: OriginConfig = {
  status: 'success',
  icon: <Icon className="fb-icon--check text-4xl text-white" />,
  title: { id: 'page.confirmation.default.title', defaultMessage: 'Success' },
  description: {
    id: 'page.confirmation.default.description',
    defaultMessage: 'Something went right.',
  },
};

const Confirmation: React.FC = () => {
  const origin = getUrlParam('origin') as OriginEnum;
  const config = ORIGIN_CONFIG[origin] ?? DEFAULT_CONFIG;

  return (
    <div className="flex justify-center items-center bg-cover bg-center bg-home relative min-h-screen">
      <StatusCard
        status={config.status}
        header={<FormattedMessage {...config.title} />}
        icon={config.icon}
        description={<FormattedMessage {...config.description} />}
      />
    </div>
  );
};

export default Confirmation;